<template>
  <div
    ref="rubricsListRef"
    class="rubrics"
    @click.stop
  >
    <div class="rubrics-list">
      <div
        v-for="group in visibleRubricsMap"
        :key="getRubricGroupKey(group)"
        class="rubric-item"
      >
        <div
          v-if="group.title && group.title !== t('rubrics_popup.top_title')"
          class="rubric-item__title"
        >
          {{ group.title }}
        </div>

        <UiTags
          v-if="group.tags.length"
          :options="getTagOptions(group.tags)"
          :class="['rubric-tags-font', { 'rubric-tags': group?.isHideRubrics }]"
          @tag-click="(index) => handleTagClick(index, group)"
        />
        <div
          v-else
          class="rubric__nothing-found"
        >
          {{ t('rubrics_popup.nothingFound') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref, watch } from 'vue';
  import { UiTags } from '@experience/core-tag-ui';
  import type { LocationQueryRaw } from 'vue-router';
  import type { ExtendedForChipsTagType, ExtendedPartTagType, ListingTagMap } from '@/entities/rubrics';
  import { useI18n } from '@experience/i18n';
  import ru from '../i18n/ru.json';
  import { createTagIdString } from '@/entities/rubrics';
  import { trackEvent } from '@/entities/sogu';
  import type { ContextGeoSubject } from '@/shared/types/geography';
  import {
    isMoreRubricTag,
    mapPopupRubricsToTagOptions,
    type MoreRubricTag,
    type PopupRubricTag,
  } from '@/entities/rubrics/popup/lib/mapPopupRubricsToTagOptions';
  import { useRubricsSearchHighlight } from '@/entities/rubrics/popup/lib/useRubricsSearchHighlight';

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  type ListingTagMapWithParams = {
    title: string | null;
    tags: PopupRubricTag[];
    id?: number;
    isHideRubrics?: boolean;
  };

  interface Props {
    rubricsMap: ListingTagMap[];
    selectedRubric: ExtendedPartTagType | null;
    defaultSoguFields: ContextGeoSubject;
    numberVisibleTags: number;
    inputValue: string;
    filterQuery?: LocationQueryRaw;
  }

  interface Emits {
    (e: 'select-rubric', rubric: ExtendedPartTagType): void;
    (e: 'input', value: { input: string; tag_id: string | undefined }): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    filterQuery: () => ({}),
  });

  const emits = defineEmits<Emits>();

  const rubricsListRef = ref<HTMLElement | null>(null);
  const rubricMapWithParams = ref<ListingTagMapWithParams[]>([]);
  const searchInput = computed(() => props.inputValue);
  const selectedSlug = computed(() => props.selectedRubric?.value || '');

  useRubricsSearchHighlight(rubricsListRef, searchInput);

  const moreCount = (tags: PopupRubricTag[]) => {
    if (tags.length > props.numberVisibleTags) {
      return tags.length - props.numberVisibleTags;
    }

    return 0;
  };

  const createMoreRubricTag = (count: number, uniqueId: string): MoreRubricTag => ({
    label: `${t('rubrics_popup.moreSmall')} ${count}`,
    value: `${t('rubrics_popup.moreSmall')} ${uniqueId}`,
    isMoreButton: true,
  });

  const isExtendedRubricTag = (rubric: PopupRubricTag): rubric is ExtendedForChipsTagType => {
    return !isMoreRubricTag(rubric);
  };

  const isVisibleSelectRubric = (tags: ExtendedForChipsTagType[]) => {
    if (!props.selectedRubric) {
      return true;
    }

    const currEl = tags.findIndex((item) => item.value === props.selectedRubric?.value);

    return currEl < props.numberVisibleTags;
  };

  const setParamsForRubrics = () => {
    rubricMapWithParams.value = props.rubricsMap.map((rubric, idx) => {
      return {
        ...rubric,
        id: idx,
        isHideRubrics: !!moreCount(rubric.tags) && isVisibleSelectRubric(rubric.tags),
      };
    });
  };

  const visibleRubricsMap = computed(() => {
    const normalizeRubricMap = rubricMapWithParams.value.map((rubric) => {
      let normalizeTags = rubric.tags;
      const moreRubricCount = moreCount(rubric.tags);

      if (moreRubricCount && rubric.isHideRubrics) {
        const uniqueId = crypto.randomUUID();
        normalizeTags = [
          ...rubric.tags.slice(0, props.numberVisibleTags || rubric.tags.length),
          createMoreRubricTag(moreRubricCount, uniqueId),
        ];
      }

      return {
        ...rubric,
        tags: normalizeTags,
      };
    });

    if (!searchInput.value.length) {
      return normalizeRubricMap;
    }

    const result = props.rubricsMap.reduce<ListingTagMap[]>((acc, group) => {
      const tags = group.tags.filter(
        (tag: ExtendedForChipsTagType) =>
          tag?.name && tag?.name.toLowerCase().includes(searchInput.value.toLowerCase()),
      );

      if (tags.length) {
        acc.push({
          title: group.title,
          tags,
        });
      }

      return acc;
    }, []);

    const unionTagsResult: ListingTagMapWithParams = {
      title: null,
      tags: [],
    };

    result.forEach((item: ListingTagMap) => {
      unionTagsResult.tags.push(...item.tags);
    });

    return [unionTagsResult];
  });

  const getRubricGroupKey = (group: ListingTagMapWithParams) => {
    return `${group.id}-${group.tags.length}-${group.isHideRubrics}`;
  };

  const getTagOptions = (tags: PopupRubricTag[]) => {
    return mapPopupRubricsToTagOptions(tags, selectedSlug.value, props.filterQuery);
  };

  const handleTagClick = (index: number, group: ListingTagMapWithParams) => {
    const rubric = group.tags[index];

    if (!rubric) {
      return;
    }

    if (isMoreRubricTag(rubric)) {
      if (group.id !== undefined) {
        rubricMapWithParams.value[group.id].isHideRubrics = false;
      }

      trackEvent('listing:themes-popup:more-button.click', {
        ...props.defaultSoguFields.soguStamp,
        theme_name: group.title || '',
      });

      return;
    }

    const payload: ExtendedPartTagType = {
      value: rubric.value,
      label: rubric.label || rubric.name || '',
      counter: rubric.counter,
      url: rubric.url,
      id: rubric.id,
      is_tours_included: rubric.is_tours_included ?? false,
    };

    trackEvent('listing:themes-popup:category.click', {
      ...props.defaultSoguFields.soguStamp,
      tag_id: rubric.id,
      theme_name: group.title || '',
    });

    emits('select-rubric', payload);
  };

  onMounted(() => {
    setParamsForRubrics();
  });

  watch(
    () => props.rubricsMap,
    () => {
      setParamsForRubrics();
    },
    {
      immediate: true,
      deep: true,
    },
  );

  watch(
    () => searchInput.value,
    (val) => {
      if (!val) {
        return;
      }

      setTimeout(() => {
        if (val === searchInput.value) {
          emits('input', {
            input: searchInput.value,
            tag_id: createTagIdString(visibleRubricsMap.value[0].tags.filter(isExtendedRubricTag)) || undefined,
          });
        }
      }, 1000);
    },
  );
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .rubrics-list {
    display: flex;
    flex-direction: column;
    gap: 32px;
    width: 100%;
    padding-bottom: 16px;

    :deep(.highlight) {
      background: var(--alias-color-background-accent-orange-light);
    }
  }

  .rubric-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .rubric-item__title {
    @include typography.base-500-400-semi;

    @include adaptivity.tablet {
      @include typography.base-400-400-semi;
    }
  }

  .rubric__nothing-found {
    margin-top: 8px;
    color: var(--alias-color-text-subtle);
    text-align: center;

    @include typography.base-500-500-regular;

    @include adaptivity.mobile {
      margin-top: 0;
    }
  }

  .rubric-tags-font {
    :deep(.ui-tag) {
      max-width: inherit;
    }

    :deep(.ui-tag__body) {
      max-width: inherit;
    }

    :deep(.ui-tag__label) {
      max-width: inherit;

      @include typography.base-400-400-regular;
    }

    :deep(.ui-tag__counter) {
      @include typography.base-400-400-regular;
    }

    :deep(.ui-tag--selected) {
      .ui-tag__label {
        color: var(--comp-tag-color-label-selected);
      }

      .ui-tag__counter {
        color: var(--comp-tag-color-counter-selected);
      }
    }
  }

  .rubric-tags {
    :deep(.ui-tag) {
      &:focus-visible {
        border: none;
        box-shadow: none;
      }
    }

    :deep(.ui-tag:last-child) {
      background: none;

      &:not(.ui-tag--disabled):hover {
        background: var(--comp-tag-color-background-hover);
      }

      .ui-tag__label {
        color: var(--comp-button-ghost-color-text-accent);
      }
    }
  }
</style>
